import { Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import S3ImageComponent from './S3ImageComponent';

export const S3Image = Node.create({
    name: 's3Image',
    group: 'block',
    draggable: true,
    selectable: true,
    atom: true,
    inline: false,
    allowGapCursor: true,

    addAttributes() {
        return {
            src: { default: null },
            key: { default: null },
            alt: { default: null },
            uploadId: { default: null },
            // Image dimensions for skeleton placeholders (prevents layout shift)
            width: { 
                default: null,
                parseHTML: element => element.getAttribute('data-width') ? parseInt(element.getAttribute('data-width'), 10) : null,
            },
            height: { 
                default: null,
                parseHTML: element => element.getAttribute('data-height') ? parseInt(element.getAttribute('data-height'), 10) : null,
            },
            size: {
                default: 'full',
                parseHTML: element => element.getAttribute('data-size'),
            },
            textAlign: {
                default: 'left',
                parseHTML: element => element.getAttribute('data-text-align'),
            }
        };
    },

    parseHTML() {
        return [{
            tag: 'img[data-s3-key]',
            getAttrs: dom => ({
                src: dom.getAttribute('src'),
                key: dom.getAttribute('data-s3-key'),
                alt: dom.getAttribute('alt'),
                uploadId: dom.getAttribute('data-upload-id'),
                width: dom.getAttribute('data-width') ? parseInt(dom.getAttribute('data-width'), 10) : null,
                height: dom.getAttribute('data-height') ? parseInt(dom.getAttribute('data-height'), 10) : null,
                size: dom.getAttribute('data-size') || 'full',
                textAlign: dom.getAttribute('data-text-align') || 'left',
            }),
        }];
    },

    addNodeView() {
        return ReactNodeViewRenderer(S3ImageComponent);
    },

    // Fallback renderHTML for getHTML() calls
    renderHTML({ HTMLAttributes }) {
        const sizes = {
            small: 'w-1/4',
            medium: 'w-1/2',
            large: 'w-3/4',
            full: 'w-full'
        };

        const alignments = {
            left: 'mr-auto',
            center: 'mx-auto',
            right: 'ml-auto'
        };

        const sizeClass = sizes[HTMLAttributes.size || 'full'];
        const alignClass = alignments[HTMLAttributes.textAlign || 'left'];
        const isUploading = !!HTMLAttributes.uploadId;

        // Outer wrapper - always full width for selection highlight
        const wrapperClasses = [
            's3-image-wrapper',
            'node-s3Image',
            'relative',
            'block',
            'mt-4',
            'mb-1',
            'rounded-lg',
            'transition-colors',
            '[.ProseMirror-focused_&.ProseMirror-selectednode]:bg-neutral-50',
            '[.ProseMirror-focused_&.ProseMirror-selectednode]:dark:bg-neutral-900/50',
        ].join(' ');

        // Inner container - has size and alignment
        const containerClasses = [
            's3-image-container',
            'block',
            sizeClass,
            alignClass,
        ].join(' ');

        const imageClasses = [
            'rounded-lg',
            'w-full',
            'transition-all',
            'transition-opacity',
            'duration-500',
            '[.ProseMirror-focused_.ProseMirror-selectednode_&]:outline',
            '[.ProseMirror-focused_.ProseMirror-selectednode_&]:outline-2',
            '[.ProseMirror-focused_.ProseMirror-selectednode_&]:outline-violet-500',
            '[.ProseMirror-focused_.ProseMirror-selectednode_&]:outline-offset-4',
            isUploading ? 'animate-pulse' : '',
        ].filter(Boolean).join(' ');

        // Always set aspect-ratio as CSS custom property for skeleton sizing
        // CSS will apply it only when image has no src
        const imgWidth = HTMLAttributes.width || 16;
        const imgHeight = HTMLAttributes.height || 9;
        const imageStyle = `--img-aspect-ratio: ${imgWidth} / ${imgHeight};`;

        return ['div', { 
            class: wrapperClasses,
            'data-type': 's3-image',
            draggable: 'true'
        }, 
            ['div', { class: containerClasses },
                // Image element - shows skeleton styling when no src via CSS
                ['img', {
                    ...HTMLAttributes,
                    'data-s3-key': HTMLAttributes.key,
                    'data-upload-id': HTMLAttributes.uploadId,
                    'data-width': HTMLAttributes.width,
                    'data-height': HTMLAttributes.height,
                    'data-size': HTMLAttributes.size,
                    'data-text-align': HTMLAttributes.textAlign,
                    'data-loaded': 'false',
                    onload: "this.dataset.loaded='true'",
                    onerror: "this.dataset.loaded='true'",
                    class: imageClasses,
                    style: imageStyle,
                }]
            ]
        ];
    },

    renderText({ node }) {
        return node.attrs.alt || '[Image]';
    },

    addCommands() {
        return {
            setS3Image: attributes => ({ commands }) => {
                return commands.insertContent({
                    type: this.name,
                    attrs: attributes
                });
            },
            updateS3Image: attributes => ({ commands }) => {
                return commands.updateAttributes(this.name, attributes);
            }
        };
    },
});

// Helper function to strip S3 image URLs before saving
export const stripS3ImageUrls = (content) => {
    if (!content || typeof content !== 'object') return content;

    if (content.type === 's3Image' && content.attrs) {
        const { src, ...restAttrs } = content.attrs;
        content.attrs = restAttrs;
    }

    if (content.content) {
        content.content = content.content.map(node => stripS3ImageUrls(node));
    }

    return content;
}; 